import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { Box, Flex, Image } from '@theme-ui/components';
import { useMutation, useQuery } from '@apollo/client';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import InfoIcon from '@app/assets/icons/InfoIcon';
import ErrorBoundary from '@app/components/ErrorBoundary';
import FormattedMoney from '@app/components/FormattedMoney';
import Loader from '@app/components/Loader';
import { H2 } from '@app/components/typography';
import { useGetCartId } from '@app/context/CartIdProvider/CartIdProvider';
import type {
  AddProductsToCartMutation,
  AddProductsToCartMutationVariables,
} from '@app/hooks/__generated__/AddProductsToCartMutation';
import { ADD_PRODUCTS_TO_CART_MUTATION } from '@app/hooks/useAddProductsToCart';
import useLocaleWithFallback from '@app/hooks/useLocaleWithFallback';
import { GiftCard } from '@app/pages/Pricing/components/GiftCard/GiftCard';
import type {
  GiftCardsQuery_magento2_products_items_Magento2_GiftCardProduct_giftcardAmounts as GiftCardAmountVariant,
  GiftCardsQuery,
  GiftCardsQuery_magento2_products_items_Magento2_GiftCardProduct,
  GiftCardsQuery_magento2_products_items_Magento2_GiftCardProduct_giftcardAmounts,
} from '@app/pages/Pricing/components/GiftProductLayout/__generated__/GiftCardsQuery';
import { GET_GIFT_CARDS_QUERY } from '@app/pages/Pricing/components/GiftProductLayout/GiftProductConfigurableLayout.queries';
import GiftProductInputForm from '@app/pages/Pricing/components/GiftProductLayout/GiftProductInputForm';
import { CurrentPrice } from '@app/pages/Pricing/components/Price/styles';
import { StockDotGreen } from '@app/pages/Pricing/components/StockMessage/styles';
import { routes } from '@app/routes/shop';
import theme from '@app/styles/theme';
import { rem } from '@app/styles/theme/layout';
import { Magento2_CurrencyEnum as CURRENCY } from '@__generated__/globalTypes';

import type { PricingPage_content_pricing_pages_layout_item_pricing_pages_single_layout_product as DirectusProduct } from '../../__generated__/PricingPage';
import AddToCartErrors from '../AddToCartErrors';
import { LayoutSubtitle } from '../ProductLayout/styles';
import messages from './GiftProductLayout.messages';

const GIFT_FORM_ID = 'GIFT_FORM_ID';
const CART_ROUTE_NAME = 'cart';

// TODO: use components/Button once it supports type="submit"
const StyledButton = styled.button`
  background: ${theme.colors.lightBlue};
  color: #fff;
  padding: ${rem(8)} ${rem(14)};
  border: 0;
  transition: all 0.4s ease;
  line-height: 1.5;
  cursor: pointer;
  font-weight: 500;
`;

type GiftProductConfigurableLayoutProps = {
  product: DirectusProduct;
  sku: string;
};

const GiftProductConfigurableLayout = ({
  product,
  sku,
}: GiftProductConfigurableLayoutProps) => {
  const { cartId } = useGetCartId();
  const history = useHistory();
  const form = useForm();
  const { language } = useParams<{
    language: string;
  }>();
  const [sortedGiftCardItemAmounts, setSortedGiftCardItemAmounts] = useState<
    (GiftCardsQuery_magento2_products_items_Magento2_GiftCardProduct_giftcardAmounts | null)[]
  >([]);

  const { findTranslationByKeyWithFallback } = useLocaleWithFallback();
  const { title } = product;
  const subtitle = findTranslationByKeyWithFallback({
    translations: product?.translations,
    key: 'subtitle',
  });

  const [selectedGiftCard, setSelectedGiftCard] =
    useState<GiftCardAmountVariant | null>();

  const { data, loading } = useQuery<GiftCardsQuery>(GET_GIFT_CARDS_QUERY, {
    variables: {
      sku: sku,
    },
  });

  const [addProductsToCart, addProductsResult] = useMutation<
    AddProductsToCartMutation,
    AddProductsToCartMutationVariables
  >(ADD_PRODUCTS_TO_CART_MUTATION);

  useEffect(() => {
    const cartRoute = routes.find(
      (route) => route.name === CART_ROUTE_NAME,
    )?.path;

    const result = addProductsResult?.data?.magento2?.addProductsToCart;
    if (result && cartRoute && !addProductsResult.error) {
      const cartUrl = generatePath(cartRoute, {
        language,
      });
      history.push(cartUrl);
    }
  }, [addProductsResult, history, language]);

  const giftCardItems = data?.magento2?.products?.items ?? [];
  const giftCardItem =
    giftCardItems[0] as GiftCardsQuery_magento2_products_items_Magento2_GiftCardProduct;
  const giftCardItemAmounts = giftCardItem?.giftcardAmounts;

  useEffect(() => {
    if (giftCardItemAmounts && giftCardItemAmounts.length > 0) {
      const sortedGiftCardItemAmounts = [...giftCardItemAmounts].sort(
        (x, y) => {
          if (x && x.value && y && y.value) {
            return x.value - y.value;
          }
          return 0;
        },
      );

      setSortedGiftCardItemAmounts(sortedGiftCardItemAmounts);

      if (sortedGiftCardItemAmounts[0]) {
        setSelectedGiftCard(sortedGiftCardItemAmounts[0]);
      }
    }
  }, [giftCardItemAmounts]);

  const currency = data?.magento2?.storeConfig?.baseCurrencyCode;

  const handleOnAddToCartClick = async (data) => {
    const {
      senderName,
      senderEmailAddress,
      recipientName,
      recipientEmailAddress,
      giftMessage,
    } = data;
    const mutationVariables = {
      cartId: cartId || '',
      cartItems: [
        {
          quantity: 1,
          sku: sku,
          entered_options: [
            {
              uid: 'Z2lmdGNhcmQvZ2lmdGNhcmRfc2VuZGVyX25hbWU=',
              value: senderName,
            },
            {
              uid: 'Z2lmdGNhcmQvZ2lmdGNhcmRfc2VuZGVyX2VtYWls',
              value: senderEmailAddress,
            },
            {
              uid: 'Z2lmdGNhcmQvZ2lmdGNhcmRfcmVjaXBpZW50X25hbWU=',
              value: recipientName,
            },
            {
              uid: 'Z2lmdGNhcmQvZ2lmdGNhcmRfcmVjaXBpZW50X2VtYWls',
              value: recipientEmailAddress,
            },
            {
              uid: 'Z2lmdGNhcmQvZ2lmdGNhcmRfbWVzc2FnZQ==',
              value: giftMessage,
            },
            {
              uid: 'Z2lmdGNhcmQvY3VzdG9tX2dpZnRjYXJkX2Ftb3VudA==',
              value: `${selectedGiftCard?.value}` || 0,
            },
          ],
        },
      ],
    };
    await addProductsToCart({ variables: mutationVariables });
  };

  if (loading) {
    return (
      <Flex py={4} sx={{ justifyContent: 'center' }}>
        <Loader color={theme.colors.lighterBlue} size={30} />
      </Flex>
    );
  }

  return (
    <ErrorBoundary>
      <Box mb={5}>
        <AddToCartErrors />
        <Box sx={{ width: '100%' }} mb={5}>
          <H2 as="h1" data-testid="pricing-product-layout-title">
            {title}
          </H2>
          <LayoutSubtitle>{subtitle}</LayoutSubtitle>
        </Box>
        <Flex sx={{ flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <Box sx={{ width: ['100%', '30%'] }} mb={[4, 0]} pr={[0, 3]}>
            <Image
              sx={{ maxWidth: '100%' }}
              data-testid="pricing-product-layout-image"
              src={giftCardItem?.image?.url ?? undefined}
              alt=""
            />
          </Box>
          <Flex
            mb={[5, 0]}
            sx={{
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              width: ['100%', '70%'],
            }}
          >
            {Boolean(sortedGiftCardItemAmounts?.length) && (
              <FormProvider {...form}>
                <Box sx={{ width: ['100%', '100%', '65%'] }} mb={[0]} pb={[0]}>
                  <Flex
                    sx={{
                      display: 'inline-flex',
                      flexWrap: 'wrap',
                      gap: rem(12),
                    }}
                  >
                    {sortedGiftCardItemAmounts?.map((giftCard) => (
                      <GiftCard
                        key={giftCard?.uid}
                        giftCardItem={giftCard}
                        currency={currency ?? CURRENCY.USD}
                        isSelected={giftCard?.uid === selectedGiftCard?.uid}
                        setSelectedGiftCard={setSelectedGiftCard}
                      />
                    ))}
                  </Flex>
                  <Box
                    mt={[3]}
                    css={css`
                      font-size: ${rem(14)};
                      line-height: 1.5;
                    `}
                  >
                    <Flex
                      sx={{
                        alignItems: 'flex-start',
                        gap: rem(10),
                      }}
                    >
                      <InfoIcon fill={theme.colors.info} width={rem(48)} />
                      <FormattedMessage {...messages.giftCardInformation} />
                    </Flex>
                  </Box>
                  <Box mt={[3]}>
                    <GiftProductInputForm
                      handleOnAddToCartClick={handleOnAddToCartClick}
                      giftFormId={GIFT_FORM_ID}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{ width: ['100%', '100%', '35%'] }}
                  mb={[5, 0]}
                  mt={[5, 0]}
                  px={[1, 0, 30]}
                >
                  {selectedGiftCard && (
                    <>
                      <CurrentPrice data-testid="product-price">
                        <FormattedMoney
                          value={selectedGiftCard.value}
                          currency={currency}
                        />
                      </CurrentPrice>
                      <Box my={15}>
                        <StockDotGreen />
                        <FormattedMessage {...messages.availableImmediately} />
                      </Box>

                      <Flex
                        sx={{
                          justifyContent: 'flex-start',
                          flexDirection: 'column',
                        }}
                      >
                        <Box>
                          <StyledButton
                            data-testid={`add-to-cart-button-${product?.sku}`}
                            type="submit"
                            form={GIFT_FORM_ID}
                            onClick={form.handleSubmit(handleOnAddToCartClick)}
                          >
                            <FormattedMessage {...messages.addToCart} />
                          </StyledButton>
                        </Box>
                      </Flex>
                    </>
                  )}
                </Box>
              </FormProvider>
            )}
            {!sortedGiftCardItemAmounts.length && (
              <Flex
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <H2>
                  <FormattedMessage {...messages.notAvailableYet} />
                </H2>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Box>
    </ErrorBoundary>
  );
};

export default GiftProductConfigurableLayout;
